import { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Button, Chip, Alert, CircularProgress, IconButton, TextField,
  MenuItem, Divider, Tooltip, Stack, InputAdornment, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { listMovimientos, Movimiento } from "../services/reconciliations";
import { importeSearchText } from "../lib/importeSearch";
import { rangoPorDefecto, esRangoPorDefecto, TOPE_PAGINAS, FILAS_POR_PAGINA, TOPE_FILAS } from "../lib/bindRango";

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 2 });
}

/** CP-421: qué dice la fila sobre reversas. En la reversa: apareada / ambigua / sin
 *  aparear (con el original si está cargado en pantalla). En el original: reversada. */
function ReversaChip({ m, porKey }: { m: Movimiento; porKey: Map<string, Movimiento> }) {
  if (m.reversada_por) {
    const r = porKey.get(m.reversada_por);
    return (
      <Tooltip title={`La plata volvió: reversa ${r ? `del ${r.fecha_valor} (${fmtMoney(r.importe)})` : m.reversada_por}${m.reversada_at ? ` · detectada ${m.reversada_at.slice(0, 16).replace("T", " ")} UTC` : ""}. Hay que reliquidar (CP-421).`}>
        <Chip label="Reversada" size="small" sx={{ fontWeight: 700, fontSize: 11, height: 22, color: "#B91C1C", bgcolor: "#FEE2E2" }} />
      </Tooltip>
    );
  }
  if (!m.reversa_estado) return <>—</>;
  if (m.reversa_estado === "apareada") {
    const o = m.reversa_de ? porKey.get(m.reversa_de) : undefined;
    return (
      <Tooltip title={o ? `Reversa de la transferencia del ${o.fecha_valor} a ${o.contraparte_nombre ?? "?"} (${o.contraparte_cuit ?? "?"}), ref ${o.referencia ?? "—"}` : `Reversa de ${m.reversa_de}`}>
        <Chip label={`Reversa de ${o ? (o.contraparte_nombre ?? o.fecha_valor) : "…"}`} size="small"
          sx={{ fontWeight: 700, fontSize: 11, height: 22, color: "#065F46", bgcolor: "#D1FAE5", maxWidth: 220 }} />
      </Tooltip>
    );
  }
  if (m.reversa_estado === "ambigua") {
    return (
      <Tooltip title={`${m.reversa_candidatos?.length ?? 0} débitos por el mismo importe en ±1 día: ${(m.reversa_candidatos ?? []).map((k) => { const c = porKey.get(k); return c ? `${c.fecha_valor} ${c.contraparte_nombre ?? "?"} ref ${c.referencia ?? "—"}` : k; }).join(" | ")}. Revisar a mano.`}>
        <Chip label={`Ambigua (${m.reversa_candidatos?.length ?? 0})`} size="small" sx={{ fontWeight: 700, fontSize: 11, height: 22, color: "#B45309", bgcolor: "#FEF3C7" }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Ningún débito por el mismo importe en ±1 día. Revisar a mano.">
      <Chip label="Sin aparear" size="small" sx={{ fontWeight: 700, fontSize: 11, height: 22, color: "#B91C1C", bgcolor: "#FEE2E2" }} />
    </Tooltip>
  );
}

export function MovimientosView() {
  const [rows,    setRows]    = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [err,     setErr]     = useState<string | null>(null);
  const [account, setAccount] = useState<"45" | "84">("45");
  const [desc,    setDesc]    = useState<string>("");
  const [from,    setFrom]    = useState<string>(() => rangoPorDefecto().from);   // CP-480: últimas 24 h por defecto
  const [to,      setTo]      = useState<string>(() => rangoPorDefecto().to);
  // CP-480: la carga sin fechas llegó al tope de páginas → resultado incompleto.
  const [truncado, setTruncado] = useState(false);
  const [q,       setQ]       = useState<string>("");
  const [soloReversas, setSoloReversas] = useState(false);   // CP-421
  const [downloading, setDownloading] = useState(false);

  // Orden (default fecha valor desc) + paginado de display client-side.
  const [sortField, setSortField] = useState<"fecha_valor" | "importe">("fecha_valor");
  const [sortDir,   setSortDir]   = useState<"asc" | "desc">("desc");
  const [shown,     setShown]     = useState(100);

  const searching = !!(q.trim() || desc);

  function toggleSort(field: "fecha_valor" | "importe") {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("desc"); }
    setShown(100);
  }
  const sortArrow = (field: "fecha_valor" | "importe") =>
    sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  function commonFilter() {
    return { account, from: from || undefined, to: to || undefined, reversas: soloReversas || undefined } as const;
  }

  // CP-393: `onProgress` es opcional (la descarga no necesita mostrarlo) —
  // lo usa loadAll() para que el usuario vea cuánto va leído mientras espera.
  async function fetchAll(onProgress?: (n: number) => void, onTruncated?: () => void): Promise<Movimiento[]> {
    const all: Movimiento[] = [];
    let cur: string | undefined = undefined;
    let completo = false;
    for (let i = 0; i < TOPE_PAGINAS; i++) {  // tope de seguridad
      const r = await listMovimientos({ ...commonFilter(), limit: FILAS_POR_PAGINA, cursor: cur });
      all.push(...r.items);
      onProgress?.(all.length);
      if (!r.cursor) { completo = true; break; }
      cur = r.cursor;
    }
    // Salió por el tope con páginas pendientes → incompleto.
    if (!completo) onTruncated?.();
    return all;
  }

  async function loadAll() {
    setLoading(true); setErr(null); setLoadedCount(0);
    setTruncado(false);
    try { setRows(await fetchAll(setLoadedCount, () => setTruncado(true))); }
    catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    loadAll(); setShown(100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, from, to, soloReversas]);

  const descOptions = useMemo(
    () => Array.from(new Set(rows.map((m) => m.descripcion).filter(Boolean) as string[])).sort(),
    [rows],
  );
  const porKey = useMemo(() => new Map(rows.map((m) => [m.mov_key, m])), [rows]);
  const filtered = useMemo(() => {
    const dq = q.trim().toLowerCase();
    return rows.filter((m) => {
      if (desc && (m.descripcion ?? "") !== desc) return false;
      if (dq) {
        const hay = [
          m.contraparte_nombre, m.contraparte_cuit, m.referencia,
          importeSearchText(m.importe), fmtMoney(m.importe),
        ].map((x) => String(x ?? "").toLowerCase()).join(" ");
        if (!hay.includes(dq)) return false;
      }
      return true;
    });
  }, [rows, desc, q]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sortField === "importe") {
        const av = a.importe ?? -Infinity, bv = b.importe ?? -Infinity;
        return av < bv ? -dir : av > bv ? dir : 0;
      }
      const av = a.fecha_valor ?? "", bv = b.fecha_valor ?? "";
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  async function downloadCsv() {
    setDownloading(true); setErr(null);
    try {
      const data = searching ? filtered : await fetchAll();
      const cols: (keyof Movimiento)[] = [
        "fecha_valor", "account", "descripcion", "categoria", "contraparte_cuit",
        "contraparte_nombre", "importe", "saldo", "referencia",
      ];
      const esc = (v: any) => {
        const s = v == null ? "" : String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [cols.join(",")];
      for (const r of data) lines.push(cols.map((c) => esc((r as any)[c])).join(","));
      const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `movimientos_${account}_${from || "ini"}_${to || "hoy"}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setDownloading(false); }
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Todo lo que <b>no</b> es Transferencia Credito: débitos DEBIN, impuestos, otros créditos.
        Solo visibilidad/auditoría — no entran a conciliación.
      </Typography>

      <Box sx={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25,
        mb: 2, p: 1.5, bgcolor: "#F7F6FC",
        border: "1px solid", borderColor: "rgba(91,47,235,0.12)", borderRadius: 3,
      }}>
        <TextField size="small" select fullWidth={false} label="Cuenta" value={account}
          onChange={(e) => setAccount(e.target.value as any)} sx={{ width: 168 }}>
          <MenuItem value="45">1-749049/45</MenuItem>
          <MenuItem value="84">1-749049/84</MenuItem>
        </TextField>
        <FormControlLabel sx={{ mr: 1 }} label={<Typography variant="body2">Solo reversas</Typography>}
          control={<Switch size="small" checked={soloReversas} onChange={(e) => setSoloReversas(e.target.checked)} />} />
        <TextField size="small" select fullWidth={false} label="Descripción" value={desc}
          onChange={(e) => setDesc(e.target.value)} sx={{ width: 220 }}>
          <MenuItem value="">Todas</MenuItem>
          {descOptions.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        <TextField size="small" type="date" fullWidth={false} label="Desde" value={from}
          onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 158 }} />
        <TextField size="small" type="date" fullWidth={false} label="Hasta" value={to}
          onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 158 }} />
        {!esRangoPorDefecto(from, to) && (
          <Tooltip title="Volver al rango por defecto (últimos 7 días)">
            <Button size="small" onClick={() => { const r = rangoPorDefecto(); setFrom(r.from); setTo(r.to); }}
              sx={{ minWidth: 0, color: "text.secondary" }}>Últimas 24 h</Button>
          </Tooltip>
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        <TextField size="small" fullWidth={false} placeholder="Buscar contraparte, importe o referencia"
          value={q} onChange={(e) => setQ(e.target.value)} sx={{ width: 300 }}
          InputProps={{ startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          ), endAdornment: q ? (
            <IconButton size="small" onClick={() => setQ("")}><CloseIcon fontSize="small" /></IconButton>
          ) : undefined }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: "auto" }}>
          {loading && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary" }}>
              <CircularProgress size={14} />
              <Typography variant="caption">Buscando…</Typography>
            </Stack>
          )}
          <Tooltip title="Refresh">
            <span><IconButton size="small" onClick={() => loadAll()} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} disabled={downloading}
            onClick={downloadCsv} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
            {downloading ? "Generando…" : "Descargar CSV"}
          </Button>
        </Box>
      </Box>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
      {truncado && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Se cargaron {TOPE_FILAS.toLocaleString("es-AR")} movimientos (el máximo) y hay más: los resultados están <b>incompletos</b>.
          Acotá el rango de fechas para buscar en un período.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell onClick={() => toggleSort("fecha_valor")}
                sx={{ cursor: "pointer", userSelect: "none", fontWeight: 700 }}>
                Fecha valor{sortArrow("fecha_valor")}
              </TableCell>
              <TableCell>Cuenta</TableCell>
              <TableCell>Contraparte</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell align="right" onClick={() => toggleSort("importe")}
                sx={{ cursor: "pointer", userSelect: "none", fontWeight: 700 }}>
                Importe{sortArrow("importe")}
              </TableCell>
              <TableCell>Referencia</TableCell>
              <TableCell>Reversa</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* CP-393: `loading` manda siempre, sin importar filas viejas de un
                filtro anterior — si no, cambiar de filtro con una búsqueda de
                texto activa podía dejar la tabla sin spinner, sin filas y sin
                "sin resultados": en blanco, indistinguible de vacío. */}
            {loading && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Buscando… {loadedCount} movimientos leídos
                  </Typography>
                </Stack>
              </TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                {searching ? "Sin resultados para la búsqueda." : "Sin movimientos para el filtro."}
              </TableCell></TableRow>
            )}
            {!loading && sorted.slice(0, shown).map((m) => {
              const neg = (m.importe ?? 0) < 0;
              return (
                <TableRow key={`${m.account}#${m.mov_key}`} hover>
                  <TableCell>{m.fecha_valor ?? "—"}</TableCell>
                  <TableCell>{m.account}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.contraparte_nombre ?? "—"}</Typography>
                    {m.contraparte_cuit && (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>{m.contraparte_cuit}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={m.descripcion ?? "—"} size="small"
                      sx={{ fontWeight: 600, fontSize: 11, height: 22, borderRadius: 2,
                            color: neg ? "#B91C1C" : "#166534", bgcolor: neg ? "#FEE2E2" : "#DCFCE7" }} />
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>{m.categoria ?? "—"}</TableCell>
                  <TableCell align="right" sx={{ color: neg ? "#B91C1C" : "inherit", fontWeight: 600 }}>
                    {fmtMoney(m.importe)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{m.referencia ?? "—"}</TableCell>
                  <TableCell><ReversaChip m={m} porKey={porKey} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {loading
            ? `Buscando… ${loadedCount} movimientos leídos`
            : `Búsqueda completa — mostrando ${Math.min(shown, sorted.length)} de ${sorted.length}`}
        </Typography>
        {sorted.length > shown && !loading && (
          <Button onClick={() => setShown((s) => s + 200)} variant="outlined" size="small">Mostrar más</Button>
        )}
      </Stack>
    </Box>
  );
}
